import { Controller } from '@hotwired/stimulus'

// Floating help widget: a single launcher that opens a multi-view panel and
// drives the externally-injected (GTM) Beacon + Cookiebot widgets.
//
// The launcher is a master toggle/close: it shows "?" when everything is
// closed and "×" whenever anything is open (our panel, the AI view, or a
// vendor overlay we triggered). Clicking it always returns to the closed state.
//
// Two independent states:
//   - openClass        -> launcher shows "×" (something is open)
//   - PANEL_OPEN_CLASS  -> our own panel is visible (false while a vendor
//                          overlay such as Beacon/Cookiebot is showing)
const PANEL_OPEN_CLASS = 'help-widget--panel-open'
// Shows the embedded GitBook assistant ("Chat with docs") as its own standalone
// panel. Like a vendor overlay, opening it closes the menu panel (PANEL_OPEN).
const AI_OPEN_CLASS = 'help-widget--ai-open'
// Auto-open for discoverability: the menu opens by itself once the page has
// been free of blockers (below) for this long, unless the visitor clicks
// something first. It repeats on every page until the visitor closes or uses
// the widget, then stays quiet for the rest of the session. Skipped on phones,
// where the panel goes edge-to-edge (matches the `screen-max(550px)`
// breakpoint in help-widget.sass). Also skipped on the demo page — the
// visitor is already where the menu's "Get a demo" button leads.
const AUTO_OPEN_DELAY = 5000
const AUTO_OPEN_TICK = 500
const AUTO_OPEN_MEDIA = '(min-width: 551px)'
const AUTO_OPEN_DISMISSED_KEY = 'helpWidgetAutoOpenDismissed'
const AUTO_OPEN_SKIP_PATH = '/get-demo'
// Blockers: overlays already asking for the visitor's attention — the
// Cookiebot consent banner and OptinMonster pop-ups (both injected via GTM).
// The menu waits until neither is on screen, and clicks on them don't count
// as the visitor being active.
const AUTO_OPEN_BLOCKERS =
  '#CybotCookiebotDialog.CybotCookiebotDialogActive, html.om-position-popup'
const AUTO_OPEN_BLOCKER_UI = '#CybotCookiebotDialog, .CampaignType--popup'

export default class extends Controller {
  static targets = ['aiFrame', 'toggle']
  static classes = ['open']

  connect() {
    // `beacon` | `cookie` | null — which third-party overlay we opened.
    this.vendorOpen = null
    this.beaconCloseBound = false
    this.onDocClick = this.handleDocClick.bind(this)
    this.onKeydown = this.handleKeydown.bind(this)
    // Cookiebot has no "onClose", but fires these when the user submits a
    // choice from the renewed dialog — reset our launcher back to "?".
    this.onCookieResolved = () => {
      if (this.vendorOpen === 'cookie') this.closeAll()
    }
    window.addEventListener('CookiebotOnAccept', this.onCookieResolved)
    window.addEventListener('CookiebotOnDecline', this.onCookieResolved)
    this.hideVendorLaunchers()
    this.scheduleAutoOpen()
  }

  disconnect() {
    this.cancelAutoOpen()
    document.removeEventListener('click', this.onDocClick)
    document.removeEventListener('keydown', this.onKeydown)
    window.removeEventListener('CookiebotOnAccept', this.onCookieResolved)
    window.removeEventListener('CookiebotOnDecline', this.onCookieResolved)
  }

  get isOpen() {
    return this.element.classList.contains(this.openClass)
  }

  // --- Master toggle / close ---------------------------------------------

  toggle(event) {
    if (event) event.preventDefault()
    this.dismissAutoOpen()
    if (this.isOpen) {
      this.closeAll()
    } else {
      this.open()
    }
  }

  open() {
    this.element.classList.add(this.openClass)
    this.showMenu()
    this.toggleTarget.setAttribute('aria-expanded', 'true')
    document.addEventListener('click', this.onDocClick)
    document.addEventListener('keydown', this.onKeydown)
  }

  // Closes our panel AND tears down any vendor overlay we opened.
  closeAll() {
    if (this.vendorOpen === 'beacon') {
      if (window.Beacon) window.Beacon('close')
    } else if (this.vendorOpen === 'cookie') {
      const cb = window.Cookiebot || window.CookieConsent
      if (cb && typeof cb.hide === 'function') cb.hide()
    }
    this.vendorOpen = null

    this.element.classList.remove(
      this.openClass,
      PANEL_OPEN_CLASS,
      AI_OPEN_CLASS
    )
    this.toggleTarget.setAttribute('aria-expanded', 'false')
    document.removeEventListener('click', this.onDocClick)
    document.removeEventListener('keydown', this.onKeydown)
  }

  handleDocClick(event) {
    // Clicks inside our own launcher/panel never close it.
    if (this.element.contains(event.target)) {
      this.dismissAutoOpen()
      return
    }

    // Following a page link isn't closing the widget — let it auto-open again
    // on the next page.
    if (!event.target.closest('a[href]')) this.dismissAutoOpen()

    // Beacon renders its panel in a separate container (an iframe, so clicks
    // inside it never reach here). Close it when the click lands outside both
    // our widget and Beacon's own chrome.
    if (this.vendorOpen === 'beacon') {
      if (event.target.closest('[class*="Beacon" i], [id*="beacon" i]')) return
      this.closeAll()
      return
    }

    // Cookiebot is a modal — let it manage its own dismissal.
    if (this.vendorOpen === 'cookie') return

    this.closeAll()
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.dismissAutoOpen()
      this.closeAll()
    }
  }

  // --- Auto-open ----------------------------------------------------------

  scheduleAutoOpen() {
    if (
      this.autoOpenDismissed ||
      window.location.pathname === AUTO_OPEN_SKIP_PATH ||
      !window.matchMedia(AUTO_OPEN_MEDIA).matches
    ) {
      return
    }
    // Any click (other than on a blocker) means the visitor is already active.
    this.onEarlyClick = event => {
      if (!event.target.closest(AUTO_OPEN_BLOCKER_UI)) this.cancelAutoOpen()
    }
    document.addEventListener('click', this.onEarlyClick, true)
    this.clearSince = Date.now()
    this.autoOpenTimer = setInterval(() => this.autoOpenTick(), AUTO_OPEN_TICK)
  }

  // Keeps running after the menu opens, until the visitor interacts with the
  // widget (dismissAutoOpen) — so an open menu here is always one we opened.
  autoOpenTick() {
    if (document.querySelector(AUTO_OPEN_BLOCKERS)) {
      this.clearSince = null
      // A pop-up appeared over our menu: step aside (not a dismissal) and come
      // back once it's gone.
      if (this.isOpen) this.closeAll()
      return
    }
    if (!this.clearSince) this.clearSince = Date.now()
    if (!this.isOpen && Date.now() - this.clearSince >= AUTO_OPEN_DELAY) {
      this.open()
    }
  }

  cancelAutoOpen() {
    clearInterval(this.autoOpenTimer)
    document.removeEventListener('click', this.onEarlyClick, true)
  }

  // The visitor closed or used the widget — no more auto-opens this session.
  dismissAutoOpen() {
    this.cancelAutoOpen()
    try {
      sessionStorage.setItem(AUTO_OPEN_DISMISSED_KEY, '1')
    } catch (e) {
      // Storage unavailable — nowhere to remember the dismissal.
    }
  }

  get autoOpenDismissed() {
    try {
      return sessionStorage.getItem(AUTO_OPEN_DISMISSED_KEY) === '1'
    } catch (e) {
      // Can't remember a dismissal, so never auto-open (it would repeat on
      // every page).
      return true
    }
  }

  // --- Panel views --------------------------------------------------------

  showMenu() {
    this.element.classList.add(PANEL_OPEN_CLASS)
    this.element.classList.remove(AI_OPEN_CLASS)
  }

  // Opens GitBook as a standalone panel, closing the menu panel — same shape as
  // openBeacon: the launcher stays "×", only the surface behind it swaps.
  showAi(event) {
    if (event) event.preventDefault()
    this.element.classList.add(this.openClass, AI_OPEN_CLASS)
    this.element.classList.remove(PANEL_OPEN_CLASS)
    // Lazy-load the GitBook assistant iframe on first open (avoids loading it
    // on every page view).
    if (this.hasAiFrameTarget && !this.aiFrameTarget.src) {
      this.aiFrameTarget.src = this.aiFrameTarget.dataset.src
    }
  }

  // --- Third-party widgets (injected via GTM) -----------------------------

  openBeacon(event) {
    if (event) event.preventDefault()
    // Hide our panel, keep the launcher in its "×" state, open Beacon.
    this.element.classList.add(this.openClass)
    this.element.classList.remove(PANEL_OPEN_CLASS)
    this.vendorOpen = 'beacon'

    if (window.Beacon) {
      // Open Beacon on the right, aligned with our launcher (its dashboard
      // default is the opposite corner).
      window.Beacon('config', { display: { position: 'right' } })
      window.Beacon('open')
      window.Beacon('navigate', '/ask/message/')
      this.bindBeaconClose()
    }
  }

  openCookiePrefs(event) {
    if (event) event.preventDefault()
    this.element.classList.add(this.openClass)
    this.element.classList.remove(PANEL_OPEN_CLASS)
    this.vendorOpen = 'cookie'

    const cb = window.Cookiebot || window.CookieConsent
    if (cb && typeof cb.renew === 'function') cb.renew()
  }

  // If the user closes Beacon via its own UI, reset our launcher to "?".
  bindBeaconClose() {
    if (this.beaconCloseBound || !window.Beacon) return
    window.Beacon('on', 'close', () => {
      if (this.vendorOpen === 'beacon') this.closeAll()
    })
    this.beaconCloseBound = true
  }

  // Hide the default Beacon FAB + Cookiebot bubble so our launcher is the only
  // one in the corner. CSS handles them regardless of GTM load timing; the
  // Beacon config call is a best-effort enhancement when it's already loaded.
  hideVendorLaunchers() {
    if (window.Beacon) {
      window.Beacon('config', { display: { style: 'manual' } })
    }
  }
}
